import { Box, Typography } from '@material-ui/core'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import { makeStyles } from '@material-ui/core/styles'
import { Color, Size } from '@ts/css'
import { Maybe } from '@ts/operations'
import React from 'react'

import { Theme } from '../../lib/theming/Theme'
import HolePunchedCard from '../utils/HolePunchedCard'
import { ReleaseCardButtonVariant } from './ReleaseCardButton'

const useStyles = makeStyles<Theme, ReleaseCardCustomStyles>(
  ({ breakpoints, palette, shape, spacing }) => ({
    card: ({ borderRadius = shape.borderRadius, cardBackgroundColor, cardTextColor }) => ({
      backgroundColor: cardBackgroundColor,
      borderRadius,
      color: cardTextColor,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'space-between',
    }),
    cardActions: {
      padding: spacing(2),
      [breakpoints.up('md')]: {
        padding: spacing(2, 3, 3),
      },
    },
    cardActionsSoldOutSpacing: {
      padding: spacing(2, 0, 3),
      [breakpoints.between('sm', 'md')]: {
        padding: spacing(2, 0),
      },
    },
    cardContent: {
      '&:last-child': {
        paddingBottom: 0,
      },
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 'auto',
      padding: spacing(2, 2, 0),
      [breakpoints.between('sm', 'md')]: {
        padding: spacing(3, 2, 2),
      },
      [breakpoints.up('md')]: {
        padding: spacing(3),
      },
    },
    descriptionText: ({ descriptionTextColor }) => ({
      color: descriptionTextColor ?? palette.grey[500],
      fontSize: '.875rem',
      fontWeight: 500,
      paddingRight: spacing(2),
    }),
    price: ({ priceTextColor }) => ({
      color: priceTextColor ?? palette.grey[800],
      padding: spacing(0, 2),
      [breakpoints.up('md')]: {
        padding: spacing(2, 3, 0),
      },
    }),
    titleHeadingText: ({ titleHeadingColor }) => ({
      color: titleHeadingColor ?? palette.grey[500],
      fontSize: spacing(1.375),
      letterSpacing: '.07em',
      textTransform: 'uppercase',
    }),
    titleText: ({ titleTextColor }) => ({
      color: titleTextColor ?? palette.grey[800],
      fontSize: '1.25rem',
      fontWeight: 700,
      letterSpacing: '0.036em',
    }),
  })
)

type ReleaseCardCustomStyles = Partial<{
  borderRadius: Size
  cardBackgroundColor: Color
  cardTextColor: Color
  descriptionTextColor: Color
  priceTextColor: Color
  titleHeadingColor: Color
  titleTextColor: Color
}>

export type ReleaseCardProps = {
  button?: Maybe<JSX.Element>
  description?: Maybe<string>
  height?: string
  name: string
  price?: JSX.Element | null
  styles?: ReleaseCardCustomStyles
  titleHeading: string
  variant?: ReleaseCardButtonVariant
}

const ReleaseCard = ({
  name,
  titleHeading,
  description,
  price,
  button,
  styles = {},
  variant = 'standard',
}: ReleaseCardProps) => {
  const classes = useStyles(styles)

  return (
    <HolePunchedCard
      className={classes.card}
      elevation={0}
      height="100%"
      offset="50%"
      position="right"
      radius="2em"
    >
      <Box display="flex">
        <CardContent className={classes.cardContent}>
          <Typography className={classes.titleHeadingText}>{titleHeading}</Typography>
          <Typography gutterBottom className={classes.titleText}>
            {name}
          </Typography>
          <Typography
            gutterBottom
            className={classes.descriptionText}
            title={description || undefined}
          >
            {description}
          </Typography>
        </CardContent>
        <Box className={classes.notchContainer}>
          <Box className={classes.notch} />
        </Box>
      </Box>
      <Box>
        {price && <CardContent className={classes.price}>{price}</CardContent>}
        {button && (
          <CardActions
            classes={{
              root: classes.cardActions,
              spacing: variant === 'soldOut' ? classes.cardActionsSoldOutSpacing : '',
            }}
          >
            {button}
          </CardActions>
        )}
      </Box>
    </HolePunchedCard>
  )
}

export default ReleaseCard
